"use client";

import { useCallback, useEffect, useState } from "react";
import { useAccount } from "wagmi";

export interface Transaction {
  hash: string;
  type: "swap" | "approve" | "transfer" | "unknown";
  status: "pending" | "success" | "failed";
  from: string;
  to?: string;
  value?: string;
  tokenIn?: string;
  tokenOut?: string;
  amountIn?: string;
  amountOut?: string;
  chainId: number;
  timestamp: number;
  gasUsed?: string;
  gasPrice?: string;
  error?: string;
}
interface TransactionHistory {
  transactions: Transaction[];
  pendingTransactions: Transaction[];
  addTransaction: (tx: Omit<Transaction, "timestamp">) => void;
  updateTransaction: (hash: string, updates: Partial<Transaction>) => void;
  clearHistory: () => void;
  getTransaction: (hash: string) => Transaction | undefined;
}

const STORAGE_KEY = "henlo_tx_history";
const MAX_TRANSACTIONS = 50;

export function useTransactionHistory(): TransactionHistory {
  const { address } = useAccount();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  useEffect(() => {
    if (!address) return;
    const stored = localStorage.getItem(`${STORAGE_KEY}_${address}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Transaction[];
        const filtered = parsed.filter((tx) => tx.from.toLowerCase() === address.toLowerCase());
        setTransactions(filtered);
      } catch (_error) {}
    }
  }, [address]);
  useEffect(() => {
    if (!address || transactions.length === 0) return;
    try {
      const toStore = transactions.slice(0, MAX_TRANSACTIONS);
      localStorage.setItem(`${STORAGE_KEY}_${address}`, JSON.stringify(toStore));
    } catch (_error) {}
  }, [transactions, address]);
  const addTransaction = useCallback((tx: Omit<Transaction, "timestamp">) => {
    const newTx: Transaction = { ...tx, timestamp: Date.now() };
    setTransactions((prev) => [newTx, ...prev].slice(0, MAX_TRANSACTIONS));
  }, []);
  const updateTransaction = useCallback((hash: string, updates: Partial<Transaction>) => {
    setTransactions((prev) =>
      prev.map((tx) => (tx.hash.toLowerCase() === hash.toLowerCase() ? { ...tx, ...updates } : tx)),
    );
  }, []);
  const clearHistory = useCallback(() => {
    setTransactions([]);
    if (address) {
      localStorage.removeItem(`${STORAGE_KEY}_${address}`);
    }
  }, [address]);
  const getTransaction = useCallback(
    (hash: string): Transaction | undefined => {
      return transactions.find((tx) => tx.hash.toLowerCase() === hash.toLowerCase());
    },
    [transactions],
  );
  const pendingTransactions = transactions.filter((tx) => tx.status === "pending");
  useEffect(() => {
    if (pendingTransactions.length === 0) return;
    const checkPending = async () => {
      for (const tx of pendingTransactions) {
        try {
          if (Date.now() - tx.timestamp > 30000) {
            updateTransaction(tx.hash, { status: "success" });
          }
        } catch (_error) {}
      }
    };
    const interval = setInterval(checkPending, 5000);
    return () => clearInterval(interval);
  }, [pendingTransactions, updateTransaction]);
  return {
    transactions,
    pendingTransactions,
    addTransaction,
    updateTransaction,
    clearHistory,
    getTransaction,
  };
}
